<template>
    <Drawer ref="drawer" title="New Revenue Entry" :loading="!isLoaded" size="is-large">
        <FormField label="Title">
            <Textbox v-model="formData.name" required />
        </FormField>

        <div class="tw-grid tw-grid-cols-2 tw-gap-3 tw-mt-3">
            <FormField label="Revenue Stream">
                <Selector v-model="formData.department_id" :options="availableRevenueDepartments" />
            </FormField>
            <FormField v-if="formData.department_id" label="Subcategory">
                <Selector v-model="formData.category_id" :options="availableCategories" />
            </FormField>

            <template v-if="formData.category_id">
                <FormField label="Customer">
                    <SupplierSelector v-model="formData.supplier_id" object-label="customer" />
                </FormField>
                <FormField label="Budget Entry (optional)">
                    <Selector
                        v-model="formData.budget_entry_id"
                        :options="budgetEntries"
                        :available-options="budgetEntries"
                    />
                </FormField>

                <FormField label="Line Items" class="tw-col-span-2">
                    <LineItemBuilder
                        v-model="formData.line_items"
                        tax-rate-type="income"
                        :default-tax-rate-id="
                            selectedRevenueDepartment?.default_tax_rate_id
                                ? Number(selectedRevenueDepartment.default_tax_rate_id)
                                : undefined
                        "
                    />
                </FormField>
            </template>
        </div>
        <template #footer>
            <Button
                type="is-primary"
                class="tw-ml-auto tw-block"
                :loading="!isLoaded"
                :disabled="!isValid"
                @click="submitEntry()"
            >
                Create Revenue Entry
            </Button>
        </template>
    </Drawer>
</template>
<script lang="ts" setup>
import SupplierSelector from '@/js/components/controls/SupplierSelector.vue';
import LineItemBuilder from '@/js/components/widgets/LineItemBuilder.vue';
import { useDataStore } from '@/js/stores/DataStore.js';
import Drawer from '@/js/components/Drawer.vue';
import Button from '@/js/components/controls/Button.vue';
import FormField from '@/js/components/widgets/FormField.vue';
import Textbox from '@/js/components/controls/Textbox.vue';
import { toast, route } from '@/js/utils.js';
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';
import { Eventbus } from '@/js/utils/eventbus.ts';
import type { Department } from '@/js/types/department.ts';
import type { Category } from '@/js/types/category.ts';
import { axios } from '@/js/utils/axios.ts';
import Selector from '@/js/components/controls/Selector.vue';
import type { BudgetEntry } from '@/js/types/budgetEntry.ts';
import { capturePosthogEvent } from '@/js/composables/usePosthog.js';

const store = useDataStore();
const isLoaded = ref(false);
const availableRevenueDepartments = ref<Department[]>(
    (Object.values(store.get('current_event.revenue_departments')) as Department[]).filter(
        (department) => department.revenue_stream_type !== 0
    )
);
const drawer = useTemplateRef('drawer');
const formData = ref<{
    line_items: {
        description: string;
        quantity: number;
        unit_price: number;
        tax_rate_id: number | null;
    }[];
    name: string;
    category_id: number | null;
    supplier_id: number | null;
    budget_entry_id: number | null;
    department_id: number | null;
}>({
    name: '',
    category_id: null,
    line_items: [],
    supplier_id: null,
    budget_entry_id: null,
    department_id: null,
});
const budgetEntries = ref<BudgetEntry[]>([]);
const isValid = computed(() => {
    return (
        formData.value.category_id &&
        formData.value.name &&
        !!formData.value.line_items.filter(
            (lineItem) => lineItem.description && lineItem.quantity && lineItem.unit_price
        ).length
    );
});
const selectedRevenueDepartment = computed(() => {
    if (!formData.value.department_id) {
        return null;
    }
    return availableRevenueDepartments.value.find((department) => department.id === formData.value.department_id);
});
const availableCategories = computed(() => {
    const revenueDepartment = availableRevenueDepartments.value.find(
        (department) => department.id === formData.value.department_id
    );
    if (!revenueDepartment) {
        return [];
    }
    return (Object.values(store.get('current_event.categories')) as Category[]).filter(
        (category) => category.department_id === revenueDepartment.id && !category.is_pooled
    );
});
watch(
    () => formData.value.department_id,
    () => {
        formData.value.line_items = [
            {
                description: '',
                quantity: 0,
                unit_price: 0,
                tax_rate_id: selectedRevenueDepartment.value?.default_tax_rate_id
                    ? Number(selectedRevenueDepartment.value.default_tax_rate_id)
                    : null,
            },
        ];
        formData.value.category_id = null;
    }
);
onMounted(() => {
    Eventbus.$on('revenue:create', (payload) => {
        resetForm();
        getRevenueDepartments();
        if (payload?.department) {
            formData.value.department_id = payload.department;
        }
        if (payload?.budgetEntry) {
            formData.value.budget_entry_id = payload.budgetEntry;
        }
        if (payload?.category) {
            formData.value.category_id = payload.category;
        }
        if (payload?.supplier) {
            formData.value.supplier_id = payload.supplier;
        }
        isLoaded.value = true;
        drawer.value?.open();
    });
});
const resetForm = () => {
    formData.value = {
        line_items: [],
        name: '',
        category_id: null,
        supplier_id: null,
        budget_entry_id: null,
        department_id: null,
    };
};

watch(
    () => formData.value.category_id,
    () => {
        axios
            .get(
                route('api.account.event.entries', {
                    category_id: formData.value.category_id,
                })
            )
            .then(({ data }) => {
                budgetEntries.value = data as BudgetEntry[];
            });
    }
);
const getRevenueDepartments = () => {
    availableRevenueDepartments.value = (
        Object.values(store.get('current_event.revenue_departments')) as Department[]
    ).filter((department) => department.revenue_stream_type !== 0);
};
const submitEntry = () => {
    if (!isValid.value) {
        toast('Error', 'Please check you have entered all required fields and try to submit again', 'error');
        return;
    }
    // prevent the submit if category_id is missing
    isLoaded.value = false;
    axios
        .post(route('api.account.event.revenue.create'), formData.value)
        .then(({ data }) => {
            capturePosthogEvent('revenue_entry_created');
            Eventbus.$emit('reload:entries');
            resetForm();
            Eventbus.$emit('revenue:entry', {
                entry: data.id,
            });
            drawer.value?.close();
            Eventbus.$emit('refresh:BudgetEntry');
        })
        .finally(() => (isLoaded.value = true));
};
</script>
